if (!customElements.get('main-image-products-slider')) {
  class MainImageProductsSlider extends HTMLElement {
	    connectedCallback() {
	      if (this.initialized) return;
	      this.initialized = true;

	      this.carousel = this.querySelector('carousel-slider.main-image-products-slider__products');
	      if (!this.carousel) return;

      this.slider = this.carousel.querySelector('.slider');
      this.nextBtn = this.carousel.querySelector('.slider-nav__btn[name="next"]');
      this.slides = this.carousel.querySelectorAll('.slider__item:not([hidden])');

      if (!this.slider || this.slides.length < 2) return;

	      this.initDrag();
	      this.initAutoplay();
	    }

    disconnectedCallback() {
      this.destroyDrag();
      this.destroyAutoplay();
      this.initialized = false;
    }

    initDrag() {
      this.pointerDown = false;
      this.isCustomDragging = false;
      this.moved = false;
      this.resumeAfterDragTimeout = null;

      this.onMouseDown = (evt) => {
        if (evt.button !== 0) return;

        this.pointerDown = true;
        this.moved = false;
        this.dragStartX = evt.pageX;
        this.dragStartScroll = this.slider.scrollLeft;
        this.isNativeDrag = this.slider.classList.contains('is-grabbable');
        this.isCustomDragging = !this.isNativeDrag;

        if (this.pauseAutoplay) this.pauseAutoplay();
        if (this.isCustomDragging) this.slider.classList.add('is-grabbing');
      };

      this.onMouseMove = (evt) => {
        if (!this.pointerDown) return;

        const delta = evt.pageX - this.dragStartX;
        if (Math.abs(delta) > 3) this.moved = true;

        if (!this.isCustomDragging) return;
        evt.preventDefault();
        this.slider.scrollLeft = this.dragStartScroll - (delta * 1.4);
      };

      this.onMouseUp = () => {
        if (!this.pointerDown) return;
        this.pointerDown = false;
        this.isCustomDragging = false;
        this.isNativeDrag = false;
        this.slider.classList.remove('is-grabbing');

        if (this.resumeAutoplay) {
          if (this.resumeAfterDragTimeout) clearTimeout(this.resumeAfterDragTimeout);
          this.resumeAfterDragTimeout = setTimeout(() => {
            this.resumeAfterDragTimeout = null;
            this.resumeAutoplay();
          }, 400);
        }
      };

      this.onClickCapture = (evt) => {
        if (!this.moved) return;
        evt.preventDefault();
        evt.stopPropagation();
        this.moved = false;
      };

      this.slider.addEventListener('mousedown', this.onMouseDown);
      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
      window.addEventListener('mouseleave', this.onMouseUp);
      this.slider.addEventListener('click', this.onClickCapture, true);
    }

    destroyDrag() {
      if (!this.slider || !this.onMouseDown) return;

      this.slider.removeEventListener('mousedown', this.onMouseDown);
      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      window.removeEventListener('mouseleave', this.onMouseUp);
      this.slider.removeEventListener('click', this.onClickCapture, true);

      this.onMouseDown = null;
      this.onMouseMove = null;
      this.onMouseUp = null;
      this.onClickCapture = null;

      if (this.resumeAfterDragTimeout) {
        clearTimeout(this.resumeAfterDragTimeout);
        this.resumeAfterDragTimeout = null;
      }
    }

    initAutoplay() {
      if (this.carousel.dataset.autoplay !== 'true' || !this.nextBtn) return;

      this.autoplaySpeed = Math.max(3000, (Number(this.carousel.dataset.speed) || 5) * 1000);
      this.pauseOnHover = this.carousel.dataset.pauseOnHover !== 'false';
      this.autoplayPaused = false;
      this.autoplayTimer = null;
      this.userScrolling = false;
      this.scrollIdleTimeout = null;

      this.advance = () => {
        if (!this.isConnected) return;

        const isBusy = this.pointerDown
          || this.userScrolling
          || (this.carousel && (this.carousel.programScrollRaf || this.carousel.loopJumping || this.carousel.loopSnapping));

	        if (isBusy) {
	          return;
	        }

	        if (this.nextBtn.disabled) {
	          this.slider.scrollTo({ left: 0, behavior: 'smooth' });
	        } else {
          this.nextBtn.click();
        }
      };

      this.stopAutoplay = () => {
        if (!this.autoplayTimer) return;
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      };

      this.startAutoplay = () => {
        if (this.autoplayPaused || this.autoplayTimer) return;
        this.autoplayTimer = setInterval(this.advance, this.autoplaySpeed);
      };

      this.pauseAutoplay = () => {
        this.autoplayPaused = true;
        this.stopAutoplay();
      };

      this.resumeAutoplay = () => {
        this.autoplayPaused = false;
        this.startAutoplay();
      };

      if (this.pauseOnHover) {
        this.carousel.addEventListener('mouseenter', this.pauseAutoplay);
        this.carousel.addEventListener('mouseleave', this.resumeAutoplay);
      }

      this.carousel.addEventListener('focusin', this.pauseAutoplay);
      this.carousel.addEventListener('focusout', this.resumeAutoplay);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.resumeAutoplay();
            } else {
              this.pauseAutoplay();
            }
          });
        }, { threshold: 0.2 });
        this.observer.observe(this.carousel);
      }

      // Avoid advancing while the user is still scrolling (wheel/trackpad/momentum).
      this.onSliderScroll = () => {
        this.userScrolling = true;
        if (this.scrollIdleTimeout) clearTimeout(this.scrollIdleTimeout);
        this.scrollIdleTimeout = setTimeout(() => {
          this.scrollIdleTimeout = null;
          this.userScrolling = false;
        }, 220);
      };
      this.slider.addEventListener('scroll', this.onSliderScroll, { passive: true });

      this.startAutoplay();
    }

    destroyAutoplay() {
      if (!this.carousel) return;

      if (this.pauseOnHover && this.pauseAutoplay && this.resumeAutoplay) {
        this.carousel.removeEventListener('mouseenter', this.pauseAutoplay);
        this.carousel.removeEventListener('mouseleave', this.resumeAutoplay);
      }

      if (this.pauseAutoplay && this.resumeAutoplay) {
        this.carousel.removeEventListener('focusin', this.pauseAutoplay);
        this.carousel.removeEventListener('focusout', this.resumeAutoplay);
      }

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.onSliderScroll && this.slider) {
        this.slider.removeEventListener('scroll', this.onSliderScroll);
        this.onSliderScroll = null;
      }

      if (this.scrollIdleTimeout) {
        clearTimeout(this.scrollIdleTimeout);
        this.scrollIdleTimeout = null;
      }
      this.userScrolling = false;

      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('main-image-products-slider', MainImageProductsSlider);
}
